/**
 * Admin Pricing Page
 *
 * Centralized pricing configuration and model cost rate management.
 * Two sections:
 * 1. Subscription Pricing — tier pricing with inline edit
 * 2. Model Cost Rates — provider_cost vs user_charge per model
 *
 * @module @humanizer/studio/components/admin/AdminPricing
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../contexts/ApiContext';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface PricingEntry {
  key: string;
  label: string;
  current: number;
  default: number;
  isOverridden: boolean;
}

interface CostRate {
  provider: string;
  modelId: string;
  inputCostPerMtok: number;
  outputCostPerMtok: number;
  effectiveFrom: string;
  effectiveUntil: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function AdminPricing() {
  const api = useApi();

  // Pricing state
  const [pricing, setPricing] = useState<PricingEntry[]>([]);
  const [pricingLoading, setPricingLoading] = useState(true);

  // Cost rates state
  const [costRates, setCostRates] = useState<CostRate[]>([]);
  const [costRatesLoading, setCostRatesLoading] = useState(true);

  // Edit state
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<number>(0);
  const [saving, setSaving] = useState(false);

  // Error/success
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ─────────────────────────────────────────────────────────────────────────
  // DATA FETCHING
  // ─────────────────────────────────────────────────────────────────────────

  const fetchPricing = useCallback(async () => {
    setPricingLoading(true);
    try {
      const result = await api.admin.listPricing();
      setPricing(result.pricing);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pricing');
    } finally {
      setPricingLoading(false);
    }
  }, [api]);

  const fetchCostRates = useCallback(async () => {
    setCostRatesLoading(true);
    try {
      const result = await api.admin.listProviderCosts();
      setCostRates(result.rates);
    } catch (err) {
      // Non-critical - cost rates may not be available
      setCostRates([]);
    } finally {
      setCostRatesLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchPricing();
    fetchCostRates();
  }, [fetchPricing, fetchCostRates]);

  // ─────────────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────────────

  const handleEdit = (entry: PricingEntry) => {
    setEditingKey(entry.key);
    setEditValue(entry.current);
    setSuccess(null);
  };

  const handleSave = async () => {
    if (!editingKey) return;
    setSaving(true);
    setError(null);

    try {
      await api.admin.updatePricing(editingKey, editValue);
      setSuccess(`Updated ${editingKey}`);
      setEditingKey(null);
      await fetchPricing();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingKey(null);
    setSuccess(null);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────────────────────────

  const formatCents = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatMtokCost = (millicents: number): string => {
    // millicents per million tokens → dollars per million tokens
    const dollars = millicents / 100_000;
    return `$${dollars.toFixed(4)}/M`;
  };

  const formatLabel = (label: string): string => {
    return label
      .replace(/^TIER_/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // Group cost rates by provider
  const ratesByProvider = costRates.reduce((acc, rate) => {
    if (!acc[rate.provider]) acc[rate.provider] = [];
    acc[rate.provider].push(rate);
    return acc;
  }, {} as Record<string, CostRate[]>);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="admin-pricing">
      {/* Header */}
      <div className="admin-pricing__header">
        <h1 className="admin-pricing__title">Pricing & Cost Rates</h1>
      </div>

      {/* Alerts */}
      {error && (
        <div className="admin-alert admin-alert--error">
          <span className="admin-alert__icon">!</span>
          <div className="admin-alert__content">
            <p className="admin-alert__message">{error}</p>
          </div>
        </div>
      )}
      {success && (
        <div className="admin-alert admin-alert--success">
          <span className="admin-alert__icon">OK</span>
          <div className="admin-alert__content">
            <p className="admin-alert__message">{success}</p>
          </div>
        </div>
      )}

      {/* Section 1: Subscription Pricing */}
      <div className="admin-pricing__section">
        <h2 className="admin-pricing__section-title">Subscription Pricing</h2>
        <p className="admin-pricing__section-description">
          Tier pricing in cents. Changes take effect immediately for new subscriptions.
        </p>

        {pricingLoading ? (
          <div className="admin-loading">
            <span className="admin-loading__spinner" />
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Config Key</th>
                <th>Current</th>
                <th>Default</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pricing.map((entry) => (
                <tr key={entry.key}>
                  <td>
                    <span className="admin-pricing__key-label">{formatLabel(entry.label)}</span>
                    <span className="admin-pricing__key-id">{entry.key}</span>
                  </td>
                  <td>
                    {editingKey === entry.key ? (
                      <input
                        type="number"
                        className="admin-form__input admin-form__input--inline"
                        value={editValue}
                        onChange={(e) => setEditValue(parseInt(e.target.value, 10) || 0)}
                        min={0}
                        autoFocus
                      />
                    ) : (
                      <span className="admin-pricing__value">{formatCents(entry.current)}</span>
                    )}
                  </td>
                  <td className="admin-pricing__default">{formatCents(entry.default)}</td>
                  <td>
                    {entry.isOverridden ? (
                      <span className="admin-badge admin-badge--warning">Overridden</span>
                    ) : (
                      <span className="admin-badge admin-badge--info">Default</span>
                    )}
                  </td>
                  <td>
                    {editingKey === entry.key ? (
                      <div className="admin-pricing__actions">
                        <button
                          className="btn btn--primary btn--sm"
                          onClick={handleSave}
                          disabled={saving}
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={handleCancel}
                          disabled={saving}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        className="btn btn--secondary btn--sm"
                        onClick={() => handleEdit(entry)}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Section 2: Model Cost Rates */}
      <div className="admin-pricing__section">
        <h2 className="admin-pricing__section-title">Model Cost Rates</h2>
        <p className="admin-pricing__section-description">
          Provider cost rates per million tokens (from aui_provider_cost_rates).
        </p>

        {costRatesLoading ? (
          <div className="admin-loading">
            <span className="admin-loading__spinner" />
          </div>
        ) : Object.keys(ratesByProvider).length === 0 ? (
          <div className="admin-empty">
            <h3 className="admin-empty__title">No cost rates found</h3>
            <p className="admin-empty__description">
              Run database seed to populate provider cost rates.
            </p>
          </div>
        ) : (
          Object.entries(ratesByProvider).map(([provider, rates]) => (
            <div key={provider} className="admin-pricing__provider-group">
              <h3 className="admin-pricing__provider-name">{provider}</h3>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Input Cost</th>
                    <th>Output Cost</th>
                    <th>Effective From</th>
                  </tr>
                </thead>
                <tbody>
                  {rates
                    .filter(r => !r.effectiveUntil)
                    .sort((a, b) => a.modelId.localeCompare(b.modelId))
                    .map((rate) => (
                      <tr key={`${rate.provider}-${rate.modelId}`}>
                        <td className="admin-pricing__model-id">{rate.modelId}</td>
                        <td>{formatMtokCost(rate.inputCostPerMtok)}</td>
                        <td>{formatMtokCost(rate.outputCostPerMtok)}</td>
                        <td className="admin-pricing__date">
                          {new Date(rate.effectiveFrom).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
